import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import { waLink } from '@/config/site';
import OrcamentoFormShell from './components/OrcamentoFormShell';
import { PERFIS } from './components/formConfigs';
import type { PerfilId } from './components/formConfigs';

const PASSOS = [
  'Você informa o perfil, o produto e a quantidade estimada.',
  'Nossa equipe avalia os modelos compatíveis com a sua demanda.',
  'Retornamos com informações comerciais, dados de fabricante e próximos passos.',
];

export default function OrcamentoPage() {
  const [searchParams] = useSearchParams();
  const perfilParam = searchParams.get('perfil');
  const perfilInicial: PerfilId =
    perfilParam === 'orgao' || perfilParam === 'arquiteto' ? perfilParam : 'empresa';
  const [perfilAtivo, setPerfilAtivo] = useState<PerfilId>(perfilInicial);

  useEffect(() => {
    const alvo = document.getElementById('formulario-orcamento');
    if (perfilParam && alvo) {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [perfilParam]);

  const perfil = PERFIS.find((item) => item.id === perfilAtivo) ?? PERFIS[0];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="bg-foreground-950 pt-24 md:pt-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20">
          <span className="text-primary-400 text-[11px] font-semibold tracking-[0.2em] uppercase">
            Solicitação comercial
          </span>
          <h1 className="mt-3 text-[32px] md:text-[52px] font-semibold text-white leading-[1.05] tracking-tight max-w-3xl">
            Diga o que o ambiente precisa. A gente avalia os itens compatíveis.
          </h1>
          <p className="mt-5 text-[15px] md:text-[16px] text-white/65 leading-relaxed max-w-2xl font-light">
            Atendemos empresas, órgãos públicos e escritórios de arquitetura em Pernambuco e
            região. Escolha o perfil abaixo para enviar sua solicitação com os dados certos.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={waLink('projetos')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary-600 text-white text-[14px] font-medium px-6 py-3 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <span className="text-[12.5px] text-white/45 font-light">
              Atendimento online 24 horas · Showroom em Caruaru com hora marcada
            </span>
          </div>
        </div>
      </section>

      <section id="formulario-orcamento" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14">
            <div className="min-w-0">
              {/* Seletor de perfil */}
              <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70 mb-8">
                {PERFIS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setPerfilAtivo(item.id)}
                    className={`px-4 md:px-5 py-2 rounded-full text-[12.5px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      perfilAtivo === item.id
                        ? 'bg-primary-600 text-white'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {item.titulo}
                  </button>
                ))}
              </div>

              <h2 className="text-[24px] md:text-[30px] font-semibold text-foreground-900 leading-tight tracking-tight">
                {perfil.titulo}
              </h2>
              <p className="mt-3 text-[14.5px] text-foreground-500 leading-relaxed font-light max-w-xl">
                {perfil.resumo}
              </p>

              <div className="mt-8">
                <OrcamentoFormShell
                  key={perfil.id}
                  formId={perfil.formId}
                  submitAddr={perfil.submitAddr}
                  campos={perfil.campos}
                  submitLabel={perfil.submitLabel}
                  nota={perfil.nota}
                />
              </div>
            </div>

            <aside className="min-w-0">
              <div className="bg-background-50 rounded-2xl border border-background-200/60 p-6 md:p-8">
                <h3 className="text-[15px] font-semibold text-foreground-900">O que acontece depois</h3>
                <ul className="mt-5 space-y-4">
                  {PASSOS.map((passo, indice) => (
                    <li key={passo} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 rounded-full bg-primary-600 text-white text-[11px] font-semibold flex items-center justify-center">
                        {indice + 1}
                      </span>
                      <span className="text-[13.5px] text-foreground-600 leading-relaxed font-light">
                        {passo}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-background-200/70">
                  <p className="text-[12.5px] text-foreground-400 font-light leading-relaxed">
                    Consulte modelos, características técnicas e condições de fornecimento.
                    Documentação de fabricante é disponibilizada conforme o modelo consultado.
                  </p>
                </div>
              </div>

              <div className="mt-5 bg-foreground-950 rounded-2xl p-6 md:p-8">
                <h3 className="text-[15px] font-semibold text-white">Prefere falar direto?</h3>
                <p className="mt-2 text-[13px] text-white/60 font-light leading-relaxed">
                  O atendimento é feito pelo proprietário. Mensagens sobre cadeiras, mesas,
                  auditórios e projetos já chegam com o contexto certo.
                </p>
                <div className="mt-5 space-y-2">
                  <a
                    href={waLink('cadeiras')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 rounded-lg bg-white/5 hover:bg-white/10 px-4 py-3 transition-colors cursor-pointer"
                  >
                    <span className="text-[13px] text-white/85">Cadeiras corporativas</span>
                    <i className="ri-arrow-right-line text-white/40"></i>
                  </a>
                  <a
                    href={waLink('auditorio')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 rounded-lg bg-white/5 hover:bg-white/10 px-4 py-3 transition-colors cursor-pointer"
                  >
                    <span className="text-[13px] text-white/85">Auditórios e plenários</span>
                    <i className="ri-arrow-right-line text-white/40"></i>
                  </a>
                  <a
                    href={waLink('orgaoPublico')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 rounded-lg bg-white/5 hover:bg-white/10 px-4 py-3 transition-colors cursor-pointer"
                  >
                    <span className="text-[13px] text-white/85">Órgãos públicos</span>
                    <i className="ri-arrow-right-line text-white/40"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}